import { useState } from 'react';
import { Tooltip } from './ChartFrame';
import { linearScale, useWidth } from './scale';

export interface ScatterPanel {
  id: string;
  label: string;
  color: string;
  points: { x: number; y: number; note?: string }[];
}

/**
 * Small multiples of predicted vs realised reward, one panel per agent so no
 * two agent colours share a plot. The diagonal is perfect prediction.
 */
export function ScatterGrid({ panels, xLabel, yLabel }: { panels: ScatterPanel[]; xLabel: string; yLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const cols = width < 520 ? 2 : 4;
  const gap = 12;
  const size = Math.floor((width - gap * (cols - 1)) / cols);
  return (
    <div ref={ref} className="scatter-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
      {panels.map((p) => (
        <ScatterPanelView key={p.id} panel={p} size={Math.max(120, size)} xLabel={xLabel} yLabel={yLabel} />
      ))}
    </div>
  );
}

function ScatterPanelView({ panel, size, xLabel, yLabel }: { panel: ScatterPanel; size: number; xLabel: string; yLabel: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const pad = 22;
  const x = linearScale(-1, 1, pad, size - 8);
  const y = linearScale(-1, 1, size - pad, 8);
  const h = hover != null ? panel.points[hover] : null;
  return (
    <div className="scatter-cell viz">
      <div className="scatter-title">
        <i className="tt-key" style={{ background: panel.color }} />
        {panel.label}
        <span className="muted"> · {panel.points.length}</span>
      </div>
      <div style={{ position: 'relative' }}>
        <svg width={size} height={size} role="img" aria-label={`${panel.label}: ${yLabel} vs ${xLabel}`}>
          <line className="grid" x1={x(-1)} x2={x(1)} y1={y(0)} y2={y(0)} />
          <line className="grid" x1={x(0)} x2={x(0)} y1={y(-1)} y2={y(1)} />
          <line className="axis" x1={x(-1)} y1={y(-1)} x2={x(1)} y2={y(1)} />
          <text className="tick" x={x(1)} y={size - 6} textAnchor="end">
            {xLabel} →
          </text>
          <text className="tick" x={4} y={14}>
            ↑ {yLabel}
          </text>
          {panel.points.map((pt, i) => (
            <g key={i}>
              <circle cx={x(pt.x)} cy={y(pt.y)} r={3.5} fill={panel.color} fillOpacity={0.85} stroke="var(--surface)" strokeWidth={1.5} />
              <circle
                cx={x(pt.x)}
                cy={y(pt.y)}
                r={12}
                fill="transparent"
                onPointerEnter={() => setHover(i)}
                onPointerLeave={() => setHover(null)}
              />
            </g>
          ))}
        </svg>
        {h && (
          <Tooltip x={x(h.x)} y={y(h.y)} width={size}>
            <strong>{h.y >= 0 ? '+' : '−'}{Math.abs(h.y).toFixed(2)}</strong>
            <span className="tt-row">realised · predicted {h.x >= 0 ? '+' : '−'}{Math.abs(h.x).toFixed(2)}</span>
            {h.note && <span className="tt-time">{h.note}</span>}
          </Tooltip>
        )}
      </div>
    </div>
  );
}
